import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import debounce from "lodash/debounce";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import Tooltipable from "~/components/effects/tooltipable";
import SearchInput from "~/components/forms/SearchInput";
import { EDITOR_TYPES } from "~/constants/EditorTypes";
import { captureEvent } from "~/helpers/capture_posthog_event";
import { checkFeatureFlag } from "~/helpers/FeatureFlagChecker";
import { channelsForEditor } from "../lib/editorTypeChannels";
import normalizeProduct from "../lib/normalizeProduct";
import { ModalStates } from "../states/ModalStates";
import ErrorState from "./ErrorState";
import GeneratingState from "./GeneratingState";
import InitialState from "./InitialState";
import MathTierWarning from "./library/MathTierWarning";
import PromptsList from "./library/PromptsList";
import SelectPromptCategory from "./library/SelectPromptCategory";
import SuggestionsPlaceholder from "./SuggestionsPlaceholder";

const TAB_KEYS = {
  COMPOSE: "compose",
  LIBRARY: "library",
  SUGGESTIONS: "suggestions",
};

// Mirrors usePaginatedSearch's debounceDelay so the library search bar
// debounces like the campaigns/labels search bars.
const SEARCH_DEBOUNCE_MS = 350;

const ModalContent = ({
  state,
  dispatch,
  markdownContentRef,
  handleInputChange,
  handlePromptClick,
  handleGenerateText,
  handleStop,
  handleInsert,
  handleRegenerate,
  handleNewPrompt,
  handleRetry,
  selectedCategory = null,
  onSelectedCategoryChange,
  onPromptsLoadingChange,
  selectedTier,
  onTierChange,
  leadId,
  editorType,
  product,
  mode,
  contextDepth,
  onContextDepthChange,
  outputFormat,
  onOutputFormatChange,
  showFormatToggle,
  toggleVoiceInput,
  templateFlags,
  applyTemplateDefaults,
}) => {
  const [activeTab, setActiveTab] = useState(TAB_KEYS.COMPOSE);
  const [promptsLoading, setPromptsLoading] = useState(false);

  // BK-3229 R3 contextual filtering. The library is narrowed down to templates
  // that match the module the user is currently in and the channel tags of
  // the editor that opened the modal. `moduleTag` + `channels` are stable for
  // the lifetime of the modal (editor + product don't change mid-session).
  const moduleTag = useMemo(() => normalizeProduct(product), [product]);
  const channels = useMemo(() => channelsForEditor(editorType), [editorType]);

  const handlePromptsLoadingChange = useCallback(
    (loading) => {
      setPromptsLoading(loading);
      onPromptsLoadingChange?.(loading);
    },
    [onPromptsLoadingChange],
  );

  // Library keyword search. `searchInput` is the immediate field value;
  // `searchTerm` is debounced before it drives the prompts fetch.
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSetSearchTerm = useMemo(
    () => debounce(setSearchTerm, SEARCH_DEBOUNCE_MS),
    [],
  );
  useEffect(
    () => () => debouncedSetSearchTerm.cancel(),
    [debouncedSetSearchTerm],
  );

  const handleSearchChange = useCallback(
    (e) => {
      const { value } = e.target;
      setSearchInput(value);
      debouncedSetSearchTerm(value);
    },
    [debouncedSetSearchTerm],
  );

  const handleClearSearch = useCallback(() => {
    debouncedSetSearchTerm.cancel();
    setSearchInput("");
    setSearchTerm("");
  }, [debouncedSetSearchTerm]);

  const handleTabSelect = useCallback(
    (key) => {
      if (key === TAB_KEYS.SUGGESTIONS) {
        return;
      }
      setActiveTab(key);
      captureEvent("ai_assist_tab_switched", {
        tab: key,
        editor_type: editorType,
        module: product,
        channel: EDITOR_TYPES[editorType]?.channel,
      });
    },
    [editorType, product],
  );

  // BK-3229 R4 AC 4.3: emergency rollback for the auto-population behaviour.
  // When the flag is off, template clicks still fill the textarea but
  // the three selectors stay at their browser-memory / system-default
  // values (pre-Phase-4 behaviour). Default rollout is `true` for all
  // plans — the flag exists so we can disable without redeploy if the
  // defaults turn out to produce worse output than expected.
  const templateDefaultsEnabled = checkFeatureFlag(
    process.env.SHAKAPACKER_PUBLIC_AI_ASSIST_TEMPLATE_DEFAULTS_FLAG,
  );

  const handleLibraryPromptClick = useCallback(
    (prompt) => {
      // Atomic: textarea + selectors + template flags + tab switch all happen
      // in the same event handler, so React 18 batches them into a single
      // paint — the user never sees an intermediate state.
      //
      // BK-3229 R4 AC 7.1.2: pass templateDefaultsEnabled into
      // handlePromptClick so useAIAssist's snapshot of templateDefaults
      // matches what was actually applied. If we passed the prompt object's
      // raw default_* through unconditionally, onFinish would compute
      // user_overrode_* against defaults the user never saw.
      handlePromptClick(prompt, { applyDefaults: templateDefaultsEnabled });
      if (templateDefaultsEnabled) {
        applyTemplateDefaults(prompt);
      }

      // Programmatic tab switch bypasses react-bootstrap's onSelect, so
      // ai_assist_tab_switched does NOT fire — matches AC 7.1.3.
      setActiveTab(TAB_KEYS.COMPOSE);

      // R4 AC 7.1.1: emit BOTH the template's declared defaults AND the
      // values actually applied. Today they're identical because there's
      // no plan-fallback logic (Gap 4 — pending plan-tier verification).
      // When fallback ships, applied_* will diverge from template_default_*
      // for cohorts whose plan disables the recommended tier.
      const applied = templateDefaultsEnabled
        ? {
            model_tier: prompt.default_model_tier ?? null,
            context_depth: prompt.default_context_depth ?? null,
            format: prompt.default_format ?? null,
          }
        : { model_tier: null, context_depth: null, format: null };

      captureEvent("ai_assist_library_prompt_selected", {
        library_prompt_id: prompt.id,
        library_prompt_name: prompt.body,
        editor_type: editorType,
        module: product,
        channel: EDITOR_TYPES[editorType]?.channel,
        category_id: prompt.category?.id ?? null,
        category_name: prompt.category?.name ?? null,
        template_default_model_tier: prompt.default_model_tier ?? null,
        template_default_context_depth: prompt.default_context_depth ?? null,
        template_default_format: prompt.default_format ?? null,
        applied_model_tier: applied.model_tier,
        applied_context_depth: applied.context_depth,
        applied_format: applied.format,
        template_defaults_enabled: templateDefaultsEnabled,
      });
    },
    [
      handlePromptClick,
      applyTemplateDefaults,
      editorType,
      product,
      templateDefaultsEnabled,
    ],
  );

  // Data-driven: server sends recommends_reasoning_tier on each category.
  // Avoids coupling UI to display names (typos, renames, i18n).
  const showMathTierWarning =
    selectedCategory?.recommends_reasoning_tier === true;

  return (
    <div className="modal-body">
      <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey={TAB_KEYS.COMPOSE}>
              <FontAwesomeIcon icon="far fa-pen" className="mr-1" />
              Compose
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={TAB_KEYS.LIBRARY}>
              <FontAwesomeIcon icon="far fa-book" className="mr-1" />
              Library
            </Nav.Link>
          </Nav.Item>
          {leadId && (
            <Nav.Item>
              <Tooltipable
                text="Coming soon in the next update"
                placement="top"
              >
                <Nav.Link eventKey={TAB_KEYS.SUGGESTIONS} disabled>
                  <FontAwesomeIcon icon="far fa-lightbulb" className="mr-1" />
                  Suggestions
                </Nav.Link>
              </Tooltipable>
            </Nav.Item>
          )}
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey={TAB_KEYS.COMPOSE}>
            {state.status !== ModalStates.INITIAL &&
              state.messages?.some((m) => m.role === "assistant") && (
                <GeneratingState
                  state={state}
                  markdownContentRef={markdownContentRef}
                />
              )}

            {state.message && <ErrorState state={state} />}

            <InitialState
              state={state}
              dispatch={dispatch}
              handleInputChange={handleInputChange}
              handleGenerateText={handleGenerateText}
              handleStop={handleStop}
              handleInsert={handleInsert}
              handleRegenerate={handleRegenerate}
              handleNewPrompt={handleNewPrompt}
              handleRetry={handleRetry}
              leadId={leadId}
              editorType={editorType}
              mode={mode}
              selectedTier={selectedTier}
              onTierChange={onTierChange}
              contextDepth={contextDepth}
              onContextDepthChange={onContextDepthChange}
              outputFormat={outputFormat}
              onOutputFormatChange={onOutputFormatChange}
              showFormatToggle={showFormatToggle}
              toggleVoiceInput={toggleVoiceInput}
              templateFlags={templateFlags}
            />
          </Tab.Pane>

          <Tab.Pane eventKey={TAB_KEYS.LIBRARY} mountOnEnter>
            <div className="row">
              <div className="mb-3 col-12">
                <SelectPromptCategory
                  onCategorySelected={onSelectedCategoryChange}
                  selectedCategory={selectedCategory}
                  isDisabled={promptsLoading}
                  moduleTag={moduleTag}
                  channels={channels}
                />
              </div>
              <div className="mb-3 col-12">
                <SearchInput
                  value={searchInput}
                  onChange={handleSearchChange}
                  onClear={handleClearSearch}
                  placeholder="Search templates by name or description..."
                  isLoading={promptsLoading}
                />
              </div>
            </div>
            {showMathTierWarning && <MathTierWarning />}
            <PromptsList
              handlePromptClick={handleLibraryPromptClick}
              selectedCategory={selectedCategory}
              onLoadingChange={handlePromptsLoadingChange}
              moduleTag={moduleTag}
              channels={channels}
              searchTerm={searchTerm}
            />
          </Tab.Pane>

          {leadId && (
            <Tab.Pane eventKey={TAB_KEYS.SUGGESTIONS} mountOnEnter>
              <SuggestionsPlaceholder />
            </Tab.Pane>
          )}
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

ModalContent.propTypes = {
  // Only the keys this component actually reads are documented here; the
  // full reducer state shape lives in useAIAssist and is intentionally
  // opaque to consumers.
  state: PropTypes.shape({
    status: PropTypes.oneOf(["INITIAL", "GENERATING", "FINISHED", "ERROR"]),
    messages: PropTypes.arrayOf(
      PropTypes.shape({
        role: PropTypes.string,
      }),
    ),
    message: PropTypes.string,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  markdownContentRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
  handleInputChange: PropTypes.func.isRequired,
  handlePromptClick: PropTypes.func.isRequired,
  handleGenerateText: PropTypes.func.isRequired,
  handleStop: PropTypes.func.isRequired,
  handleInsert: PropTypes.func.isRequired,
  handleRegenerate: PropTypes.func.isRequired,
  handleNewPrompt: PropTypes.func.isRequired,
  handleRetry: PropTypes.func.isRequired,
  selectedCategory: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
  }),
  onSelectedCategoryChange: PropTypes.func.isRequired,
  onPromptsLoadingChange: PropTypes.func,
  selectedTier: PropTypes.string.isRequired,
  onTierChange: PropTypes.func.isRequired,
  leadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  editorType: PropTypes.string,
  product: PropTypes.string,
  mode: PropTypes.string,
  contextDepth: PropTypes.string.isRequired,
  onContextDepthChange: PropTypes.func.isRequired,
  outputFormat: PropTypes.string.isRequired,
  onOutputFormatChange: PropTypes.func.isRequired,
  showFormatToggle: PropTypes.bool,
  toggleVoiceInput: PropTypes.func,
  templateFlags: PropTypes.shape({
    model: PropTypes.bool,
    context: PropTypes.bool,
    format: PropTypes.bool,
  }),
  applyTemplateDefaults: PropTypes.func.isRequired,
};

export default ModalContent;
